import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { commitAssignments, getAssignmentSummary } from "../api/endpoints/assignments"
import { listAgents } from "../api/endpoints/agents"
import { listClients } from "../api/endpoints/clients"
import { createTarget, listTargets, updateTarget } from "../api/endpoints/targets"
import { listUsers } from "../api/endpoints/users"
import { AgentPerformanceDrawer } from "../components/agent_performance_drawer"
import { CapacityGauge } from "../components/capacity_gauge"
import { ClientDetailDrawer } from "../components/client_detail_drawer"
import { PerformanceBandBadge } from "../components/performance_band_badge"
import { PipelineMiniBar } from "../components/pipeline_mini_bar"
import {
  Button,
  DataTable,
  EmptyState,
  FormField,
  Input,
  Modal,
  PageSection,
  Select,
  StatCard,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
  Textarea,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDate, formatDateTime, formatEnumLabel } from "../utils/formatting"

const ADMIN_VISIBLE_CATEGORIES = ["S1", "CATEGORY_A", "CATEGORY_B", "CATEGORY_C"]
const CATEGORY_FILTER = ADMIN_VISIBLE_CATEGORIES.join(",")
const REASSIGNMENT_REASONS = [
  "AGENT_OVERLOAD",
  "AGENT_LEAVE",
  "AGENT_DEACTIVATION",
  "PERFORMANCE_ISSUE",
  "GEOGRAPHIC_REBALANCING",
  "CLIENT_REQUEST",
  "OTHER",
] as const

interface PendingAssignmentItem {
  id: string
  mode: "ASSIGN" | "REASSIGN"
  destinationAgentId: string
  destinationAgentName: string
  sourceAgentId?: string
  sourceAgentName?: string
  clients: Array<{
    clientId: string
    clientName: string
    outstandingBalance: number
    currency?: string
  }>
  totalOutstanding: number
  nextAssignedClients: number
  nextOutstandingAmount: number
  errors?: string[]
}

function TabButton({
  active,
  children,
  onClick,
}: {
  active: boolean
  children: React.ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`rounded-lg px-4 py-2 text-sm font-medium ${
        active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function progressTone(status?: string) {
  if (status === "GREEN") return "success"
  if (status === "YELLOW" || status === "AMBER") return "warning"
  if (status === "RED") return "danger"
  return "neutral"
}

function overdueTone(category?: string | null) {
  if (category === "CATEGORY_C") return "danger"
  if (category === "CATEGORY_B" || category === "CATEGORY_A") return "warning"
  if (category === "S1") return "info"
  return "neutral"
}

function formatQueueDuration(seconds?: number | null) {
  if (!seconds || seconds <= 0) {
    return "0m"
  }

  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3_600)
  const minutes = Math.floor((seconds % 3_600) / 60)

  if (days > 0) {
    return `${days}d ${hours}h`
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }

  return `${minutes}m`
}

function periodLabel(windowData?: {
  startAt?: string
  endAt?: string
  dayNumber?: number
  totalDays?: number
}) {
  if (!windowData?.startAt || !windowData?.endAt) {
    return "Current period"
  }

  return `${formatDate(windowData.startAt)} - ${formatDate(windowData.endAt)} • Day ${
    windowData.dayNumber ?? 0
  } of ${windowData.totalDays ?? 0}`
}

function unique<T>(items: T[]) {
  return [...new Set(items)]
}

function trayClientIds(items: PendingAssignmentItem[]) {
  return new Set(items.flatMap((item) => item.clients.map((client) => client.clientId)))
}

export function TargetsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const queueRef = useRef<HTMLDivElement | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN"
  const isReadOnly = !isAdmin
  const [activeTab, setActiveTab] = useState<"targets" | "assignments">(
    () =>
      !isAdmin ? "assignments" : searchParams.get("tab") === "assignments" ? "assignments" : "targets",
  )
  const [targetNotes, setTargetNotes] = useState("")
  const [targetForm, setTargetForm] = useState({
    name: "",
    scopeType: "TEAM",
    agentId: "",
    teamName: searchParams.get("team") ?? "",
    targetPercentage: "75",
    timeframe: "MONTHLY",
    overdueCategory: "CATEGORY_A",
    thresholdGreen: "0.8",
    thresholdYellow: "0.5",
  })
  const [teamFilter, setTeamFilter] = useState(searchParams.get("team") ?? "")
  const [queueSearch, setQueueSearch] = useState(searchParams.get("search") ?? "")
  const [queueSort, setQueueSort] = useState(searchParams.get("queue_sort") ?? "queue_age")
  const [agentSearch, setAgentSearch] = useState("")
  const [agentBandFilter, setAgentBandFilter] = useState(searchParams.get("band") ?? "")
  const [agentStatusFilter, setAgentStatusFilter] = useState("ACTIVE")
  const [agentView, setAgentView] = useState<"table" | "card">("table")
  const [queuePageSize, setQueuePageSize] = useState("50")
  const [agentPageSize, setAgentPageSize] = useState("25")
  const [queuePage, setQueuePage] = useState(1)
  const [agentPage, setAgentPage] = useState(1)
  const [selectedUnassignedIds, setSelectedUnassignedIds] = useState<string[]>([])
  const [selectedReassignIds, setSelectedReassignIds] = useState<string[]>([])
  const [pendingAssignments, setPendingAssignments] = useState<PendingAssignmentItem[]>([])
  const [pickerMode, setPickerMode] = useState<"assign" | "reassign" | null>(null)
  const [assignmentNote, setAssignmentNote] = useState("")
  const [reassignmentReason, setReassignmentReason] =
    useState<(typeof REASSIGNMENT_REASONS)[number]>("AGENT_OVERLOAD")
  const [trayExpanded, setTrayExpanded] = useState(true)
  const [commitOpen, setCommitOpen] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState("0")
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    searchParams.get("agent_id"),
  )

  const sourceAgentId = searchParams.get("source_agent_id") ?? ""
  const mode = searchParams.get("mode") ?? ""
  const focusAgentId = searchParams.get("focus_agent_id") ?? ""
  const requestedReassignIds = useMemo(
    () => (searchParams.get("client_ids") ?? "").split(",").filter(Boolean),
    [searchParams],
  )

  const agentsQuery = useQuery({
    queryKey: ["users", "agents", "targets-page"],
    queryFn: () => listUsers({ role: "AGENT", per_page: "200" }),
  })

  const targetsQuery = useQuery({
    queryKey: ["targets", "admin-targets"],
    queryFn: () => listTargets({ per_page: "100" }),
    enabled: isAdmin && activeTab === "targets",
  })

  const assignmentSummaryQuery = useQuery({
    queryKey: ["assignment-summary", teamFilter],
    queryFn: () => getAssignmentSummary(teamFilter ? { team: teamFilter } : undefined),
    enabled: activeTab === "assignments",
  })

  const unassignedQuery = useQuery({
    queryKey: [
      "assignment-queue",
      teamFilter,
      queueSearch,
      queueSort,
      queuePage,
      queuePageSize,
    ],
    queryFn: () =>
      listClients({
        overdue_category: CATEGORY_FILTER,
        assignment_status: "unassigned",
        per_page: queuePageSize,
        page: String(queuePage),
        sort_by: queueSort,
        sort_order: queueSort === "queue_age" ? "desc" : "asc",
        ...(queueSearch.trim() ? { search: queueSearch.trim() } : {}),
      }),
    enabled: activeTab === "assignments",
  })

  const workloadQuery = useQuery({
    queryKey: [
      "assignment-workload",
      teamFilter,
      agentSearch,
      agentBandFilter,
      agentStatusFilter,
      agentView,
      agentPage,
      agentPageSize,
    ],
    queryFn: () =>
      listAgents({
        include: "workload_summary",
        per_page: agentPageSize,
        page: String(agentPage),
        sort_by: "remaining_capacity",
        sort_order: "desc",
        ...(teamFilter ? { team: teamFilter } : {}),
        ...(agentSearch.trim() ? { search: agentSearch.trim() } : {}),
        ...(agentBandFilter ? { band: agentBandFilter } : {}),
        ...(agentStatusFilter ? { status: agentStatusFilter } : {}),
      }),
    enabled: activeTab === "assignments",
  })

  const sourceClientsQuery = useQuery({
    queryKey: ["assignment-source-clients", sourceAgentId],
    queryFn: () =>
      listClients({
        overdue_category: CATEGORY_FILTER,
        assigned_agent_id: sourceAgentId,
        per_page: "100",
        sort_by: "days_overdue",
        sort_order: "desc",
      }),
    enabled: activeTab === "assignments" && Boolean(sourceAgentId),
  })

  const createTargetMutation = useMutation({
    mutationFn: () =>
      createTarget({
        name: targetForm.name,
        scopeType: targetForm.scopeType,
        agentId: targetForm.scopeType === "AGENT" ? targetForm.agentId : undefined,
        teamName: targetForm.scopeType === "TEAM" ? targetForm.teamName : undefined,
        targetPercentage: Number(targetForm.targetPercentage),
        timeframe: targetForm.timeframe,
        overdueCategory: targetForm.overdueCategory,
        thresholdGreen: Number(targetForm.thresholdGreen),
        thresholdYellow: Number(targetForm.thresholdYellow),
        notes: targetNotes || undefined,
      }),
    onSuccess: async () => {
      setTargetForm((current) => ({ ...current, name: "", agentId: "" }))
      setTargetNotes("")
      await queryClient.invalidateQueries({ queryKey: ["targets", "admin-targets"] })
    },
  })

  const toggleTargetMutation = useMutation({
    mutationFn: ({ targetId, isActive }: { targetId: string; isActive: boolean }) =>
      updateTarget(targetId, { isActive }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["targets", "admin-targets"] })
    },
  })

  const commitMutation = useMutation({
    mutationFn: () =>
      commitAssignments({
        items: pendingAssignments.flatMap((item) =>
          item.clients.map((client) => ({
            clientId: client.clientId,
            destinationAgentId: item.destinationAgentId,
            sourceAgentId: item.sourceAgentId,
            reassignmentReason: item.sourceAgentId ? reassignmentReason : undefined,
          })),
        ),
        note: assignmentNote || undefined,
      }),
    onSuccess: async (response) => {
      const byClient = new Map<string, any>(
        (response.data?.items ?? []).map((item: any) => [item.clientId, item]),
      )

      setPendingAssignments((current) =>
        current
          .map((item) => {
            const remainingClients = item.clients.filter(
              (client) => byClient.get(client.clientId)?.status !== "COMMITTED",
            )
            if (remainingClients.length === 0) {
              return null
            }

            const errors = unique(
              remainingClients
                .map((client) => byClient.get(client.clientId)?.errorMessage)
                .filter(Boolean) as string[],
            )

            return {
              ...item,
              clients: remainingClients,
              totalOutstanding: remainingClients.reduce(
                (sum: number, client: { outstandingBalance: number }) =>
                  sum + client.outstandingBalance,
                0,
              ),
              errors,
            }
          })
          .filter(Boolean) as PendingAssignmentItem[],
      )

      setCommitOpen(false)
      setSelectedUnassignedIds([])
      setSelectedReassignIds([])
      setAssignmentNote("")
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["assignment-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["assignment-queue"] }),
        queryClient.invalidateQueries({ queryKey: ["assignment-workload"] }),
        queryClient.invalidateQueries({ queryKey: ["assignment-source-clients"] }),
        queryClient.invalidateQueries({ queryKey: ["performance-team-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["performance-leaderboard"] }),
      ])
    },
  })

  const targets = targetsQuery.data?.data ?? []
  const agents = agentsQuery.data?.data ?? []
  const assignmentSummary = assignmentSummaryQuery.data?.data
  const queueRows = unassignedQuery.data?.data ?? []
  const queueMeta = unassignedQuery.data?.meta
  const workloadRows = workloadQuery.data?.data ?? []
  const workloadMeta = workloadQuery.data?.meta
  const sourceRows = sourceClientsQuery.data?.data ?? []
  const sourceAgent = agents.find((agent: any) => agent.userId === sourceAgentId)

  const highlightedAgent = useMemo(
    () =>
      workloadRows.find((row: any) => row.agentId === focusAgentId) ??
      workloadRows.find((row: any) => row.agentId === assignmentSummary?.kpis?.mostAvailableAgent?.agentId),
    [assignmentSummary?.kpis?.mostAvailableAgent?.agentId, focusAgentId, workloadRows],
  )

  const queueOutstanding = queueRows.reduce(
    (sum: number, row: any) => sum + (row.outstandingBalance ?? 0),
    0,
  )
  const selectedUnassignedSummary = queueRows.filter((row: any) =>
    selectedUnassignedIds.includes(row.clientId),
  )
  const selectedReassignSummary = sourceRows.filter((row: any) =>
    selectedReassignIds.includes(row.clientId),
  )

  useEffect(() => {
    if (!isAdmin) {
      setActiveTab("assignments")
      const next = new URLSearchParams(searchParams)
      next.set("tab", "assignments")
      setSearchParams(next, { replace: true })
    }
  }, [isAdmin, searchParams, setSearchParams])

  useEffect(() => {
    if (requestedReassignIds.length > 0) {
      setSelectedReassignIds(requestedReassignIds)
    }
  }, [requestedReassignIds.join(",")])

  useEffect(() => {
    setSelectedAgentId(searchParams.get("agent_id"))
  }, [searchParams])

  useEffect(() => {
    if (activeTab !== "assignments") {
      return
    }

    const refreshMs = Number(autoRefresh)
    if (!refreshMs) {
      return
    }

    const handle = window.setInterval(() => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["assignment-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["assignment-queue"] }),
        queryClient.invalidateQueries({ queryKey: ["assignment-workload"] }),
      ])
    }, refreshMs)

    return () => window.clearInterval(handle)
  }, [activeTab, autoRefresh, queryClient])

  function updateRouteState(updates: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(updates)) {
      if (value) {
        next.set(key, value)
      } else {
        next.delete(key)
      }
    }
    setSearchParams(next)
  }

  function refreshAssignments() {
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ["assignment-summary"] }),
      queryClient.invalidateQueries({ queryKey: ["assignment-queue"] }),
      queryClient.invalidateQueries({ queryKey: ["assignment-workload"] }),
      queryClient.invalidateQueries({ queryKey: ["assignment-source-clients"] }),
    ])
  }

  function selectedRowsForMode(nextMode: "assign" | "reassign") {
    return nextMode === "assign" ? selectedUnassignedSummary : selectedReassignSummary
  }

  function queueSelectionForAgent(agent: any, nextMode: "assign" | "reassign") {
    const rows = selectedRowsForMode(nextMode)
    if (rows.length === 0) {
      return
    }

    const existing = trayClientIds(pendingAssignments)
    const newClients = rows
      .filter((row: any) => !existing.has(row.clientId))
      .map((row: any) => ({
        clientId: row.clientId,
        clientName: row.fullName ?? row.clientName,
        outstandingBalance: row.outstandingBalance ?? 0,
        currency: row.currency,
      }))

    if (newClients.length === 0) {
      return
    }

    const totalOutstanding = newClients.reduce(
      (sum: number, client: { outstandingBalance: number }) => sum + client.outstandingBalance,
      0,
    )

    setPendingAssignments((current) => [
      ...current,
      {
        id: `${agent.agentId}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        mode: nextMode === "assign" ? "ASSIGN" : "REASSIGN",
        destinationAgentId: agent.agentId,
        destinationAgentName: agent.agentName,
        sourceAgentId: nextMode === "reassign" ? sourceAgentId : undefined,
        sourceAgentName: nextMode === "reassign" ? sourceAgent?.fullName : undefined,
        clients: newClients,
        totalOutstanding,
        nextAssignedClients: (agent.assignedClients ?? 0) + newClients.length,
        nextOutstandingAmount: (agent.outstandingAmount ?? 0) + totalOutstanding,
      },
    ])

    if (nextMode === "assign") {
      setSelectedUnassignedIds([])
    } else {
      setSelectedReassignIds([])
    }
    setPickerMode(null)
    setTrayExpanded(true)
  }

  const targetSummary = useMemo(() => {
    const active = targets.filter((target: any) => target.isActive)
    const onTarget = active.filter((target: any) => target.progress?.status === "GREEN").length
    const belowTarget = active.filter((target: any) => target.progress?.status === "RED").length
    const avgRate =
      active.length > 0
        ? active.reduce(
            (sum: number, target: any) => sum + (target.progress?.actualPercentage ?? 0),
            0,
          ) / active.length
        : 0

    return {
      active: active.length,
      onTarget,
      belowTarget,
      avgRate: avgRate.toFixed(1),
    }
  }, [targets])

  return (
    <div className="space-y-6">
      <PageSection
        title={activeTab === "assignments" ? "Client Assignment" : "Targets & Assignments"}
        actions={
          isAdmin ? (
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
              <TabButton
                active={activeTab === "targets"}
                onClick={() => {
                  setActiveTab("targets")
                  updateRouteState({ tab: "targets" })
                }}
              >
                Targets
              </TabButton>
              <TabButton
                active={activeTab === "assignments"}
                onClick={() => {
                  setActiveTab("assignments")
                  updateRouteState({ tab: "assignments" })
                }}
              >
                Assignments
              </TabButton>
            </div>
          ) : null
        }
      >
        <p className="text-sm text-slate-500">
          {activeTab === "assignments"
            ? "Assign overdue clients to agents, manage reassignment, and balance workload across teams."
            : "Recovery target management and target performance monitoring."}
        </p>
      </PageSection>

      {activeTab === "targets" ? (
        <>
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatCard label="Active Targets" value={targetSummary.active} />
            <StatCard label="On Target" value={targetSummary.onTarget} helper="Green band" />
            <StatCard label="Below Target" value={targetSummary.belowTarget} helper="Red band" />
            <StatCard label="Aggregate Recovery Rate" value={`${targetSummary.avgRate}%`} />
          </div>

          <PageSection title="Create Target">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <FormField label="Target Name">
                <Input
                  value={targetForm.name}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </FormField>
              <FormField label="Scope">
                <Select
                  value={targetForm.scopeType}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, scopeType: event.target.value }))
                  }
                >
                  <option value="AGENT">Agent</option>
                  <option value="TEAM">Team</option>
                  <option value="ALL_AGENTS">All Agents</option>
                </Select>
              </FormField>
              <FormField label="Timeframe">
                <Select
                  value={targetForm.timeframe}
                  onChange={(event) =>
                    setTargetForm((current) => ({ ...current, timeframe: event.target.value }))
                  }
                >
                  <option value="WEEKLY">Weekly</option>
                  <option value="BI_WEEKLY">Bi-weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="CUSTOM">Custom</option>
                </Select>
              </FormField>
              {targetForm.scopeType === "AGENT" ? (
                <FormField label="Agent">
                  <Select
                    value={targetForm.agentId}
                    onChange={(event) =>
                      setTargetForm((current) => ({ ...current, agentId: event.target.value }))
                    }
                  >
                    <option value="">Select agent</option>
                    {agents.map((agent: any) => (
                      <option key={agent.userId} value={agent.userId}>
                        {agent.fullName}
                      </option>
                    ))}
                  </Select>
                </FormField>
              ) : null}
              {targetForm.scopeType === "TEAM" ? (
                <FormField label="Team Name">
                  <Input
                    value={targetForm.teamName}
                    onChange={(event) =>
                      setTargetForm((current) => ({ ...current, teamName: event.target.value }))
                    }
                  />
                </FormField>
              ) : null}
              <FormField label="Target %">
                <Input
                  type="number"
                  value={targetForm.targetPercentage}
                  onChange={(event) =>
                    setTargetForm((current) => ({
                      ...current,
                      targetPercentage: event.target.value,
                    }))
                  }
                />
              </FormField>
              <FormField label="Overdue Category">
                <Select
                  value={targetForm.overdueCategory}
                  onChange={(event) =>
                    setTargetForm((current) => ({
                      ...current,
                      overdueCategory: event.target.value,
                    }))
                  }
                >
                  <option value="S1">S-1</option>
                  <option value="CATEGORY_A">Category A</option>
                  <option value="CATEGORY_B">Category B</option>
                  <option value="CATEGORY_C">Category C</option>
                </Select>
              </FormField>
              <FormField label="Green Threshold">
                <Input
                  type="number"
                  step="0.1"
                  value={targetForm.thresholdGreen}
                  onChange={(event) =>
                    setTargetForm((current) => ({
                      ...current,
                      thresholdGreen: event.target.value,
                    }))
                  }
                />
              </FormField>
              <FormField label="Yellow Threshold">
                <Input
                  type="number"
                  step="0.1"
                  value={targetForm.thresholdYellow}
                  onChange={(event) =>
                    setTargetForm((current) => ({
                      ...current,
                      thresholdYellow: event.target.value,
                    }))
                  }
                />
              </FormField>
            </div>
            <div className="mt-3">
              <FormField label="Notes">
                <Textarea
                  rows={3}
                  value={targetNotes}
                  onChange={(event) => setTargetNotes(event.target.value)}
                />
              </FormField>
            </div>
            <div className="mt-4">
              <Button
                onClick={() => createTargetMutation.mutate()}
                disabled={createTargetMutation.isPending}
              >
                Create Target
              </Button>
            </div>
          </PageSection>

          <PageSection title="Targets Table">
            {targets.length === 0 ? (
              <EmptyState
                title="No targets configured"
                description="Create the first recovery target to start tracking team performance."
              />
            ) : (
              <div className="overflow-x-auto">
                <DataTable>
                  <TableHead>
                    <Th>Name</Th>
                    <Th>Scope</Th>
                    <Th>Timeframe</Th>
                    <Th>Progress</Th>
                    <Th>Status</Th>
                    <Th>Created By</Th>
                    <Th>Action</Th>
                  </TableHead>
                  <TableBody>
                    {targets.map((target: any) => (
                      <TableRow key={target.targetId}>
                        <Td>
                          <strong>{target.name}</strong>
                          <div className="text-xs text-slate-500">{target.targetId}</div>
                        </Td>
                        <Td>
                          {target.scopeType === "AGENT"
                            ? target.agentName
                            : target.scopeType === "TEAM"
                              ? target.teamName
                              : "All Agents"}
                        </Td>
                        <Td>
                          {target.progress?.window
                            ? `${formatDate(target.progress.window.startAt)} - ${formatDate(target.progress.window.endAt)}`
                            : target.timeframe}
                        </Td>
                        <Td>{target.progress?.actualPercentage ?? 0}%</Td>
                        <Td>
                          <StatusPill tone={progressTone(target.progress?.status)}>
                            {target.progress?.status ?? "PENDING"}
                          </StatusPill>
                        </Td>
                        <Td>{target.setByName ?? "-"}</Td>
                        <Td>
                          <Button
                            size="sm"
                            variant={target.isActive ? "secondary" : "outline"}
                            onClick={() =>
                              toggleTargetMutation.mutate({
                                targetId: target.targetId,
                                isActive: !target.isActive,
                              })
                            }
                            disabled={toggleTargetMutation.isPending}
                          >
                            {target.isActive ? "Deactivate" : "Activate"}
                          </Button>
                        </Td>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </div>
            )}
          </PageSection>
        </>
      ) : (
        <>
          <PageSection
            title="Assignment Control Bar"
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={refreshAssignments}>
                  Refresh
                </Button>
                <Select value={autoRefresh} onChange={(event) => setAutoRefresh(event.target.value)}>
                  <option value="0">Auto-refresh off</option>
                  <option value="30000">30s</option>
                  <option value="60000">1m</option>
                  <option value="300000">5m</option>
                </Select>
              </div>
            }
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Period</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {periodLabel(assignmentSummary?.window)}
                </p>
              </div>
              <FormField label="Team">
                <Select
                  value={teamFilter}
                  onChange={(event) => {
                    setTeamFilter(event.target.value)
                    updateRouteState({ team: event.target.value || null })
                  }}
                >
                  <option value="">All Teams</option>
                  {(assignmentSummary?.availableTeams ?? []).map((entry: string) => (
                    <option key={entry} value={entry}>
                      {entry}
                    </option>
                  ))}
                </Select>
              </FormField>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Last Updated</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {formatDateTime(assignmentSummary?.lastUpdatedAt)}
                </p>
              </div>
            </div>
          </PageSection>

          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
              onClick={() => queueRef.current?.scrollIntoView({ behavior: "smooth" })}
            >
              <p className="text-xs text-slate-500">Unassigned Clients</p>
              <p className="mt-2 text-2xl font-bold text-red-700">
                {assignmentSummary?.kpis?.unassignedClients ?? 0}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {formatCurrency(assignmentSummary?.kpis?.unassignedOutstandingAmount ?? 0)}
              </p>
            </button>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs text-slate-500">Longest Unassigned</p>
              <p className="mt-2 text-xl font-bold text-slate-900">
                {formatQueueDuration(
                  assignmentSummary?.kpis?.longestUnassigned?.queueAgeSeconds,
                )}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {assignmentSummary?.kpis?.longestUnassigned?.clientName ?? "No queue"}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs text-slate-500">Avg Queue Age</p>
              <p className="mt-2 text-xl font-bold text-slate-900">
                {formatQueueDuration(assignmentSummary?.kpis?.avgQueueAgeSeconds)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs text-slate-500">Total Agents</p>
              <p className="mt-2 text-xl font-bold text-slate-900">
                {assignmentSummary?.kpis?.totalAgents ?? 0}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {assignmentSummary?.kpis?.activeAgents ?? 0} active /{" "}
                {assignmentSummary?.kpis?.inactiveAgents ?? 0} inactive
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs text-slate-500">Avg Clients Per Agent</p>
              <p className="mt-2 text-xl font-bold text-slate-900">
                {(assignmentSummary?.kpis?.avgClientsPerAgent ?? 0).toFixed(1)}
              </p>
            </div>
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
              onClick={() =>
                updateRouteState({
                  focus_agent_id: assignmentSummary?.kpis?.mostOverloadedAgent?.agentId ?? null,
                })
              }
            >
              <p className="text-xs text-slate-500">Most Overloaded Agent</p>
              <p className="mt-2 text-lg font-bold text-slate-900">
                {assignmentSummary?.kpis?.mostOverloadedAgent?.agentName ?? "-"}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {assignmentSummary?.kpis?.mostOverloadedAgent?.assignedClients ?? 0} clients
              </p>
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
              onClick={() =>
                updateRouteState({
                  focus_agent_id: assignmentSummary?.kpis?.mostAvailableAgent?.agentId ?? null,
                })
              }
            >
              <p className="text-xs text-slate-500">Most Available Agent</p>
              <p className="mt-2 text-lg font-bold text-slate-900">
                {assignmentSummary?.kpis?.mostAvailableAgent?.agentName ?? "-"}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {assignmentSummary?.kpis?.mostAvailableAgent?.remainingCapacity ?? 0} capacity left
              </p>
            </button>
          </div>

          {mode === "reassign" && sourceAgentId ? (
            <PageSection title="Clients to Reassign">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-900">
                      Reassigning from {sourceAgent?.fullName ?? sourceAgentId}
                    </p>
                    <p className="text-xs text-amber-700">
                      Select clients from the source agent, then queue them to a new destination.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() =>
                      updateRouteState({
                        mode: null,
                        source_agent_id: null,
                        client_ids: null,
                      })
                    }
                  >
                    Clear Reassignment Mode
                  </Button>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <DataTable>
                    <TableHead>
                      <Th />
                      <Th>Client</Th>
                      <Th>Loan</Th>
                      <Th>Outstanding</Th>
                      <Th>Category</Th>
                      <Th>Days Overdue</Th>
                    </TableHead>
                    <TableBody>
                      {sourceRows.map((row: any) => (
                        <TableRow key={row.clientId}>
                          <Td>
                            <input
                              checked={selectedReassignIds.includes(row.clientId)}
                              type="checkbox"
                              onChange={() =>
                                setSelectedReassignIds((current) =>
                                  current.includes(row.clientId)
                                    ? current.filter((entry) => entry !== row.clientId)
                                    : [...current, row.clientId],
                                )
                              }
                            />
                          </Td>
                          <Td>
                            <button type="button" onClick={() => setSelectedClientId(row.clientId)}>
                              <span className="font-semibold text-slate-900">{row.fullName}</span>
                              <span className="block text-xs text-slate-500">{row.clientId}</span>
                            </button>
                          </Td>
                          <Td>{row.loanId}</Td>
                          <Td>{formatCurrency(row.outstandingBalance ?? 0, row.currency)}</Td>
                          <Td>
                            <StatusPill tone={overdueTone(row.overdueCategory)}>
                              {formatEnumLabel(row.overdueCategory)}
                            </StatusPill>
                          </Td>
                          <Td>{row.daysOverdue ?? 0}</Td>
                        </TableRow>
                      ))}
                    </TableBody>
                  </DataTable>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-amber-900">
                    {selectedReassignIds.length} selected •{" "}
                    {formatCurrency(
                      selectedReassignSummary.reduce(
                        (sum: number, row: any) => sum + (row.outstandingBalance ?? 0),
                        0,
                      ),
                    )}
                  </p>
                  {!isReadOnly ? (
                    <Button
                      onClick={() => setPickerMode("reassign")}
                      disabled={selectedReassignIds.length === 0}
                    >
                      Queue Reassignment
                    </Button>
                  ) : null}
                </div>
              </div>
            </PageSection>
          ) : null}

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="xl:col-span-7 space-y-6" ref={queueRef}>
              <PageSection
                title="Unassigned Client Queue"
                actions={
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      placeholder="Search name, loan ID, phone"
                      value={queueSearch}
                      onChange={(event) => setQueueSearch(event.target.value)}
                    />
                    <Select value={queueSort} onChange={(event) => setQueueSort(event.target.value)}>
                      <option value="queue_age">Queue age</option>
                      <option value="outstanding_balance">Outstanding amount</option>
                      <option value="days_overdue">Days overdue</option>
                      <option value="overdue_category">Overdue category</option>
                    </Select>
                    <Select
                      value={queuePageSize}
                      onChange={(event) => {
                        setQueuePageSize(event.target.value)
                        setQueuePage(1)
                      }}
                    >
                      <option value="25">25 rows</option>
                      <option value="50">50 rows</option>
                      <option value="100">100 rows</option>
                    </Select>
                  </div>
                }
              >
                {queueRows.length === 0 ? (
                  <EmptyState
                    title="All overdue clients are assigned"
                    description="The visible portfolio is fully covered. Reassignment remains available from the source-agent workflow."
                  />
                ) : (
                  <>
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-600">
                        {selectedUnassignedIds.length} clients selected •{" "}
                        {formatCurrency(
                          selectedUnassignedSummary.reduce(
                            (sum: number, row: any) => sum + (row.outstandingBalance ?? 0),
                            0,
                          ),
                        )}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() =>
                            setSelectedUnassignedIds(
                              selectedUnassignedIds.length === queueRows.length
                                ? []
                                : queueRows.map((row: any) => row.clientId),
                            )
                          }
                        >
                          {selectedUnassignedIds.length === queueRows.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                        {!isReadOnly ? (
                          <Button
                            onClick={() => setPickerMode("assign")}
                            disabled={selectedUnassignedIds.length === 0}
                          >
                            Assign Selected
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <DataTable>
                        <TableHead>
                          <Th />
                          <Th>Client</Th>
                          <Th>Loan</Th>
                          <Th>Outstanding</Th>
                          <Th>Category</Th>
                          <Th>Days Overdue</Th>
                          <Th>Loan Product</Th>
                          <Th>Region</Th>
                          <Th>Queue Age</Th>
                        </TableHead>
                        <TableBody>
                          {queueRows.map((row: any) => (
                            <TableRow key={row.clientId}>
                              <Td>
                                <input
                                  checked={selectedUnassignedIds.includes(row.clientId)}
                                  type="checkbox"
                                  onChange={() =>
                                    setSelectedUnassignedIds((current) =>
                                      current.includes(row.clientId)
                                        ? current.filter((entry) => entry !== row.clientId)
                                        : [...current, row.clientId],
                                    )
                                  }
                                />
                              </Td>
                              <Td>
                                <button type="button" onClick={() => setSelectedClientId(row.clientId)}>
                                  <span className="font-semibold text-slate-900">{row.fullName}</span>
                                  <span className="block text-xs text-slate-500">{row.clientId}</span>
                                </button>
                              </Td>
                              <Td>{row.loanId ?? "-"}</Td>
                              <Td>{formatCurrency(row.outstandingBalance ?? 0, row.currency)}</Td>
                              <Td>
                                <StatusPill tone={overdueTone(row.overdueCategory)}>
                                  {formatEnumLabel(row.overdueCategory)}
                                </StatusPill>
                              </Td>
                              <Td>{row.daysOverdue ?? 0}</Td>
                              <Td>{row.loanProduct ?? "-"}</Td>
                              <Td>{row.region ?? "-"}</Td>
                              <Td>{formatQueueDuration(row.queueAgeSeconds)}</Td>
                            </TableRow>
                          ))}
                        </TableBody>
                      </DataTable>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
                      <span>
                        {queueMeta?.totalItems ?? queueRows.length} clients • {formatCurrency(queueOutstanding)}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setQueuePage((current) => Math.max(1, current - 1))}
                          disabled={queuePage <= 1}
                        >
                          Previous
                        </Button>
                        <span>Page {queuePage}</span>
                        <Button
                          variant="outline"
                          onClick={() => setQueuePage((current) => current + 1)}
                          disabled={queuePage >= (queueMeta?.totalPages ?? 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </PageSection>
            </div>

            <div className="xl:col-span-5 space-y-6">
              <PageSection
                title="Agent Workload"
                actions={
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      placeholder="Search agent"
                      value={agentSearch}
                      onChange={(event) => setAgentSearch(event.target.value)}
                    />
                    <Select
                      value={agentBandFilter}
                      onChange={(event) => setAgentBandFilter(event.target.value)}
                    >
                      <option value="">All bands</option>
                      <option value="GREEN">Green</option>
                      <option value="AMBER">Amber</option>
                      <option value="RED">Red</option>
                    </Select>
                    <Select
                      value={agentStatusFilter}
                      onChange={(event) => setAgentStatusFilter(event.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="ALL">All</option>
                    </Select>
                    <div className="inline-flex rounded-lg border border-slate-200 p-1">
                      <button
                        type="button"
                        className={`rounded-md px-2 py-1 text-xs ${
                          agentView === "table" ? "bg-slate-900 text-white" : "text-slate-600"
                        }`}
                        onClick={() => setAgentView("table")}
                      >
                        Table
                      </button>
                      <button
                        type="button"
                        className={`rounded-md px-2 py-1 text-xs ${
                          agentView === "card" ? "bg-slate-900 text-white" : "text-slate-600"
                        }`}
                        onClick={() => setAgentView("card")}
                      >
                        Card
                      </button>
                    </div>
                  </div>
                }
              >
                {workloadRows.length === 0 ? (
                  <EmptyState
                    title="No agents match filters"
                    description="Adjust team, band, or active-status filters."
                  />
                ) : agentView === "card" ? (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
                    {workloadRows.map((row: any) => (
                      <div
                        key={row.agentId}
                        className={`rounded-2xl border p-4 ${
                          row.agentId === highlightedAgent?.agentId ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"
                        }`}
                      >
                        <button
                          type="button"
                          className="text-left"
                          onClick={() => updateRouteState({ agent_id: row.agentId })}
                        >
                          <p className="text-base font-semibold text-slate-900">{row.agentName}</p>
                          <p className="text-xs text-slate-500">{row.teamName ?? "No team"}</p>
                        </button>
                        <div className="mt-3 flex items-center justify-between gap-3">
                          <PerformanceBandBadge band={row.performanceBand} />
                          <span className="text-sm font-semibold text-slate-900">
                            {(row.recoveryRate ?? 0).toFixed(1)}%
                          </span>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-slate-500">Assigned</p>
                            <p className="font-semibold text-slate-900">{row.assignedClients ?? 0}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Outstanding</p>
                            <p className="font-semibold text-slate-900">
                              {formatCurrency(row.outstandingAmount ?? 0)}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4">
                          <PipelineMiniBar values={row} />
                        </div>
                        <div className="mt-4">
                          <CapacityGauge
                            assignedClients={row.assignedClients ?? 0}
                            remainingCapacity={row.remainingCapacity ?? 0}
                            status={row.capacityStatus}
                          />
                        </div>
                        {!isReadOnly && selectedUnassignedIds.length > 0 ? (
                          <div className="mt-4">
                            <Button onClick={() => queueSelectionForAgent(row, "assign")}>
                              Assign Queue
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <DataTable>
                        <TableHead>
                          <Th>Agent</Th>
                          <Th>Team</Th>
                          <Th>Assigned</Th>
                          <Th>Outstanding</Th>
                          <Th>Recovery Rate</Th>
                          <Th>Unprocessed</Th>
                          <Th>Pipeline</Th>
                          <Th>Capacity</Th>
                          <Th>Last Active</Th>
                          {!isReadOnly ? <Th>Action</Th> : null}
                        </TableHead>
                        <TableBody>
                          {workloadRows.map((row: any) => (
                            <TableRow
                              key={row.agentId}
                              className={
                                row.agentId === highlightedAgent?.agentId ? "bg-blue-50" : undefined
                              }
                            >
                              <Td>
                                <button type="button" onClick={() => updateRouteState({ agent_id: row.agentId })}>
                                  <span className="font-semibold text-slate-900">{row.agentName}</span>
                                  <span className="block text-xs text-slate-500">{row.agentId}</span>
                                </button>
                              </Td>
                              <Td>{row.teamName ?? "-"}</Td>
                              <Td>{row.assignedClients ?? 0}</Td>
                              <Td>{formatCurrency(row.outstandingAmount ?? 0)}</Td>
                              <Td>
                                <div className="space-y-2">
                                  <PerformanceBandBadge band={row.performanceBand} compact />
                                  <div className="text-xs font-semibold text-slate-900">
                                    {(row.recoveryRate ?? 0).toFixed(1)}%
                                  </div>
                                </div>
                              </Td>
                              <Td>{row.unprocessedCount ?? 0}</Td>
                              <Td>
                                <div className="min-w-[140px]">
                                  <PipelineMiniBar values={row} />
                                </div>
                              </Td>
                              <Td>
                                <div className="min-w-[120px]">
                                  <CapacityGauge
                                    assignedClients={row.assignedClients ?? 0}
                                    remainingCapacity={row.remainingCapacity ?? 0}
                                    status={row.capacityStatus}
                                  />
                                </div>
                              </Td>
                              <Td>{formatDateTime(row.lastSeenAt)}</Td>
                              {!isReadOnly ? (
                                <Td>
                                  <div className="flex flex-col gap-2">
                                    {selectedUnassignedIds.length > 0 ? (
                                      <Button size="sm" onClick={() => queueSelectionForAgent(row, "assign")}>
                                        Assign Queue
                                      </Button>
                                    ) : null}
                                    {selectedReassignIds.length > 0 && sourceAgentId ? (
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => queueSelectionForAgent(row, "reassign")}
                                      >
                                        Reassign
                                      </Button>
                                    ) : null}
                                  </div>
                                </Td>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </DataTable>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                      <span>{workloadMeta?.totalItems ?? workloadRows.length} agents</span>
                      <div className="flex items-center gap-2">
                        <Select
                          value={agentPageSize}
                          onChange={(event) => {
                            setAgentPageSize(event.target.value)
                            setAgentPage(1)
                          }}
                        >
                          <option value="25">25 rows</option>
                          <option value="50">50 rows</option>
                          <option value="100">100 rows</option>
                        </Select>
                        <Button
                          variant="outline"
                          onClick={() => setAgentPage((current) => Math.max(1, current - 1))}
                          disabled={agentPage <= 1}
                        >
                          Previous
                        </Button>
                        <span>Page {agentPage}</span>
                        <Button
                          variant="outline"
                          onClick={() => setAgentPage((current) => current + 1)}
                          disabled={agentPage >= (workloadMeta?.totalPages ?? 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </PageSection>
            </div>
          </div>

          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur">
            <div className="mx-auto max-w-[calc(100vw-3rem)] px-6 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Pending Assignments
                    <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                      {pendingAssignments.length}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">
                    Review queued changes before committing them.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => setTrayExpanded((current) => !current)}>
                    {trayExpanded ? "Collapse" : "Expand"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPendingAssignments([])}
                    disabled={pendingAssignments.length === 0}
                  >
                    Clear All
                  </Button>
                  {!isReadOnly ? (
                    <Button
                      onClick={() => setCommitOpen(true)}
                      disabled={pendingAssignments.length === 0}
                    >
                      Commit All
                    </Button>
                  ) : null}
                </div>
              </div>
              {trayExpanded ? (
                <div className="mt-4 overflow-x-auto">
                  {pendingAssignments.length === 0 ? (
                    <EmptyState
                      title="No pending assignments"
                      description="Queued assignments will appear here before commit."
                    />
                  ) : (
                    <DataTable>
                      <TableHead>
                        <Th>Client(s)</Th>
                        <Th>Outstanding</Th>
                        <Th>From</Th>
                        <Th>To</Th>
                        <Th>Agent New Load</Th>
                        <Th>Agent New Outstanding</Th>
                        <Th>Error</Th>
                        <Th />
                      </TableHead>
                      <TableBody>
                        {pendingAssignments.map((item) => (
                          <TableRow key={item.id}>
                            <Td>
                              <div className="font-semibold text-slate-900">
                                {item.clients.length === 1
                                  ? item.clients[0]?.clientName
                                  : `${item.clients.length} clients`}
                              </div>
                              <div className="text-xs text-slate-500">
                                {item.clients.map((client) => client.clientName).join(", ")}
                              </div>
                            </Td>
                            <Td>{formatCurrency(item.totalOutstanding)}</Td>
                            <Td>{item.sourceAgentName ?? "Unassigned"}</Td>
                            <Td>{item.destinationAgentName}</Td>
                            <Td>{item.nextAssignedClients}</Td>
                            <Td>{formatCurrency(item.nextOutstandingAmount)}</Td>
                            <Td className="text-xs text-red-600">
                              {item.errors?.length ? item.errors.join(" • ") : "-"}
                            </Td>
                            <Td>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  setPendingAssignments((current) =>
                                    current.filter((entry) => entry.id !== item.id),
                                  )
                                }
                              >
                                Remove
                              </Button>
                            </Td>
                          </TableRow>
                        ))}
                      </TableBody>
                    </DataTable>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      <Modal
        open={Boolean(pickerMode)}
        onClose={() => setPickerMode(null)}
        title={pickerMode === "reassign" ? "Select Destination Agent" : "Assign Selected Clients"}
        description="Choose an agent to queue this assignment into the pending tray."
      >
        <div className="space-y-3">
          {workloadRows.map((row: any) => (
            <button
              key={row.agentId}
              type="button"
              className="w-full rounded-xl border border-slate-200 p-4 text-left hover:border-blue-300 hover:bg-blue-50"
              onClick={() => queueSelectionForAgent(row, pickerMode === "reassign" ? "reassign" : "assign")}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{row.agentName}</p>
                  <p className="text-xs text-slate-500">{row.teamName ?? "No team"}</p>
                </div>
                <PerformanceBandBadge band={row.performanceBand} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-slate-500">Assigned</p>
                  <p className="font-semibold text-slate-900">{row.assignedClients ?? 0}</p>
                </div>
                <div>
                  <p className="text-slate-500">Outstanding</p>
                  <p className="font-semibold text-slate-900">
                    {formatCurrency(row.outstandingAmount ?? 0)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Recovery</p>
                  <p className="font-semibold text-slate-900">{(row.recoveryRate ?? 0).toFixed(1)}%</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        open={commitOpen}
        onClose={() => setCommitOpen(false)}
        title="Confirm Assignments"
        description={`Assigning ${pendingAssignments.reduce(
          (sum, item) => sum + item.clients.length,
          0,
        )} clients across ${unique(pendingAssignments.map((item) => item.destinationAgentId)).length} agents.`}
      >
        <div className="space-y-4">
          <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-200">
            {pendingAssignments.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div>
                  <p className="font-semibold text-slate-900">{item.destinationAgentName}</p>
                  <p className="text-xs text-slate-500">
                    {item.clients.length} clients • {formatCurrency(item.totalOutstanding)}
                  </p>
                  {item.sourceAgentName ? (
                    <p className="text-xs text-amber-700">From {item.sourceAgentName}</p>
                  ) : null}
                </div>
                <div className="text-right text-xs text-slate-500">
                  <p>New load {item.nextAssignedClients}</p>
                  <p>{formatCurrency(item.nextOutstandingAmount)}</p>
                </div>
              </div>
            ))}
          </div>
          {pendingAssignments.some((item) => item.mode === "REASSIGN") ? (
            <FormField label="Reason for reassignment">
              <Select
                value={reassignmentReason}
                onChange={(event) =>
                  setReassignmentReason(
                    event.target.value as (typeof REASSIGNMENT_REASONS)[number],
                  )
                }
              >
                {REASSIGNMENT_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {formatEnumLabel(reason)}
                  </option>
                ))}
              </Select>
            </FormField>
          ) : null}
          <FormField label="Assignment Note">
            <Textarea
              rows={3}
              value={assignmentNote}
              onChange={(event) => setAssignmentNote(event.target.value)}
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCommitOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => commitMutation.mutate()} disabled={commitMutation.isPending}>
              Confirm Assignments
            </Button>
          </div>
        </div>
      </Modal>

      <ClientDetailDrawer
        clientId={selectedClientId}
        open={Boolean(selectedClientId)}
        onClose={() => setSelectedClientId(null)}
      />

      <AgentPerformanceDrawer
        agentId={selectedAgentId}
        open={Boolean(selectedAgentId)}
        canManage={isAdmin}
        onClose={() => updateRouteState({ agent_id: null })}
      />
    </div>
  )
}
